import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Camera, ChartBar } from "@phosphor-icons/react";
import TradeFormDialog from "../components/TradeFormDialog";
import { InfoTip } from "../components/InfoTip";
import { fmtNum, fmtDateTime } from "../lib/format";

const statusColor = {
  open: "border-[hsl(var(--primary))] text-[hsl(var(--primary))]",
  pending: "border-yellow-500 text-yellow-500",
  closed: "border-border text-muted-foreground",
  cancelled: "border-border text-muted-foreground line-through",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [prices, setPrices] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    const [t, s] = await Promise.all([api.get("/trades", { params }), api.get("/stats")]);
    setTrades(t.data);
    setStats(s.data);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openSymbols = [...new Set(trades.filter((t) => t.status === "open").map((t) => t.symbol))].join(",");

  useEffect(() => {
    if (!openSymbols) { setPrices({}); return; }
    let stop = false;
    const fetchPrices = () =>
      api.get("/market/prices", { params: { symbols: openSymbols } })
        .then((r) => { if (!stop) setPrices(r.data); })
        .catch(() => {});
    fetchPrices();
    const iv = setInterval(fetchPrices, 30000);
    return () => { stop = true; clearInterval(iv); };
  }, [openSymbols]);

  const upnl = (t) => {
    const p = prices[t.symbol];
    if (t.status !== "open" || p == null) return null;
    return (t.side === "long" ? 1 : -1) * (p - t.entry_price) * t.quantity;
  };
  const totalUpnl = trades.reduce((acc, t) => {
    const u = upnl(t);
    return u == null ? acc : (acc ?? 0) + u;
  }, null);

  const fmt = (n) => fmtNum(n, 6);

  const kpis = stats ? [
    { label: "Total P/L", value: `${stats.total_pnl >= 0 ? "+" : ""}${fmt(stats.total_pnl)}`, cls: stats.total_pnl >= 0 ? "text-profit" : "text-loss", id: "kpi-total-pnl",
      tip: "Sum of the realized profit/loss of all closed trades ((exit − entry) × quantity, direction-aware)." },
    { label: "Unrealized P/L", value: totalUpnl == null ? "—" : `${totalUpnl >= 0 ? "+" : ""}${fmt(totalUpnl.toFixed(2))}`, cls: totalUpnl == null ? "text-muted-foreground" : totalUpnl >= 0 ? "text-profit" : "text-loss", id: "kpi-unrealized-pnl",
      tip: "Floating profit/loss of your open trades at the current market price. Refreshes every 30 seconds." },
    { label: "Win Rate", value: stats.win_rate != null ? `${stats.win_rate}%` : "—", cls: "text-foreground", id: "kpi-win-rate",
      tip: "Percentage of closed trades that ended with a positive P/L." },
    { label: "Open", value: stats.open_trades, cls: "text-foreground", id: "kpi-open-trades",
      tip: "Number of trades currently open in your journal." },
    { label: "Closed", value: stats.closed_trades, cls: "text-foreground", id: "kpi-closed-trades",
      tip: "Number of trades that have been closed (manually, or automatically by exchange sync)." },
  ] : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-3xl tracking-tight">Journal</h1>
        <Button data-testid="new-trade-btn" onClick={() => setFormOpen(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
          <Plus size={16} className="mr-1" weight="bold" /> New Trade
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="border border-border bg-card px-4 py-3" data-testid={k.id}>
            <div className="label-caps text-muted-foreground flex items-center gap-1.5">
              {k.label}
              <InfoTip text={k.tip} />
            </div>
            <div className={`font-mono text-2xl mt-1 ${k.cls}`}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="status-filter-select" className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground font-mono" data-testid="trades-count">{trades.length} trades</span>
      </div>

      <div className="border border-border bg-card">
        <Table data-testid="trades-table">
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="label-caps">Date</TableHead>
              <TableHead className="label-caps">Symbol</TableHead>
              <TableHead className="label-caps">Side</TableHead>
              <TableHead className="label-caps text-right">Entry</TableHead>
              <TableHead className="label-caps text-right">Qty</TableHead>
              <TableHead className="label-caps text-right">SL</TableHead>
              <TableHead className="label-caps text-right">TPs</TableHead>
              <TableHead className="label-caps text-right">P/L</TableHead>
              <TableHead className="label-caps">Status</TableHead>
              <TableHead className="label-caps">Source</TableHead>
              <TableHead className="label-caps text-right">Chart</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trades.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-muted-foreground py-12" data-testid="empty-trades-msg">
                  No trades yet. Create your first trade or import from Bybit in Settings.
                </TableCell>
              </TableRow>
            )}
            {trades.map((t) => (
              <TableRow key={t.id} data-testid={`trade-row-${t.id}`} onClick={() => navigate(`/trade/${t.id}`)}
                className="border-border cursor-pointer hover:bg-accent/40">
                <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {fmtDateTime(t.trade_date || t.created_at, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </TableCell>
                <TableCell className="font-mono font-medium">
                  <span className="flex items-center gap-2">
                    {t.symbol}
                    {t.photos?.length > 0 && <Camera size={14} className="text-muted-foreground" />}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`font-mono text-xs uppercase ${t.side === "long" ? "text-profit" : "text-loss"}`}>{t.side}</span>
                </TableCell>
                <TableCell className="text-right font-mono">{fmt(t.entry_price)}</TableCell>
                <TableCell className="text-right font-mono">{fmt(t.quantity)}</TableCell>
                <TableCell className="text-right font-mono text-loss">{t.stop_loss != null ? fmt(t.stop_loss) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-profit">{t.take_profits?.length || 0}</TableCell>
                <TableCell className={`text-right font-mono ${(() => {
                  const u = upnl(t);
                  if (t.pnl != null) return t.pnl >= 0 ? "text-profit" : "text-loss";
                  if (u != null) return u >= 0 ? "text-profit" : "text-loss";
                  return "text-muted-foreground";
                })()}`}>
                  {t.pnl != null
                    ? `${t.pnl >= 0 ? "+" : ""}${fmt(t.pnl)}`
                    : (() => {
                        const u = upnl(t);
                        return u != null ? `≈ ${u >= 0 ? "+" : ""}${fmt(u.toFixed(2))}` : "—";
                      })()}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`label-caps ${statusColor[t.status]}`}>{t.status}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-xs font-mono">{t.source}</TableCell>
                <TableCell className="text-right">
                  <button
                    data-testid={`open-chart-btn-${t.id}`}
                    title="Open in Charts"
                    onClick={(e) => { e.stopPropagation(); navigate(`/charts?symbol=${encodeURIComponent(t.symbol)}`); }}
                    className="text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors duration-150 p-1"
                  >
                    <ChartBar size={16} weight="bold" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TradeFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </div>
  );
}
